'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';

const MEMBERS = [
  {
    name: 'G-Dragon',
    slug: 'GD',
    color: '#ED609F',
    initial: 'GD',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/AMCArtistImages221/v4/d7/ad/04/d7ad0465-31c7-8e88-c79a-fb77b0d9d73a/ami-identity-e3922a1ff16fb44fe1a6e3bfba76bf7a-2025-02-25T04-52-00.145Z_cropped.png/400x400bb.png',
  },
  {
    name: 'Taeyang',
    slug: 'Taeyang',
    color: '#D7171E',
    initial: 'TY',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/b9/20/39/b92039fb-d24d-1a29-18b6-d4b6477d7916/mzl.rzdhegsk.jpg/400x400bb.png',
  },
  {
    name: 'TOP',
    slug: 'TOP',
    color: '#8FC126',
    initial: 'TOP',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/AMCArtistImages211/v4/6c/08/52/6c085280-cd9a-d53b-fc43-456111fe1057/ami-identity-4e0c8f30cd47352576cf496d1ded52db-2026-02-24T13-16-30.782Z_cropped.png/400x400bb.png',
  },
  {
    name: 'Daesung',
    slug: 'Daesung',
    color: '#F4E727',
    initial: 'DS',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/AMCArtistImages211/v4/d5/8a/61/d58a61e9-1f53-cb48-5f5f-98c5c00e2c53/ami-identity-37271ed0ebe14c45ccc2508a16282bda-2025-04-15T13-10-36.192Z_cropped.png/400x400bb.png',
  },
  {
    name: 'Seungri',
    slug: 'Seungri',
    color: '#1E92C6',
    initial: 'SR',
    image: 'https://is1-ssl.mzstatic.com/image/thumb/Features125/v4/3a/55/b2/3a55b210-95cb-89f4-89f5-bee24a68fe00/mzl.wjoudyqz.jpg/400x400bb.png',
  },
];

// Intersection Observer hook for scroll animations
function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

export default function HomeClient() {
  const t = useTranslations();
  const [mounted, setMounted] = useState(false);
  const membersSection = useInView(0.2);
  const featuresSection = useInView(0.2);
  const statsSection = useInView(0.3);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[550px] md:min-h-[650px] flex items-center justify-center overflow-hidden">
        {/* Animated Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Main radial gradient with pulse */}
          <div
            className="absolute inset-0 animate-pulse-slow"
            style={{
              background:
                'radial-gradient(ellipse 80% 50% at 50% 20%, rgba(212, 175, 55, 0.08) 0%, transparent 60%)',
            }}
          />
          {/* Floating color accents */}
          <div
            className="absolute inset-0 opacity-30 animate-float-slow"
            style={{
              background:
                'radial-gradient(circle at 20% 80%, rgba(237, 96, 159, 0.08) 0%, transparent 30%), radial-gradient(circle at 80% 70%, rgba(30, 146, 198, 0.08) 0%, transparent 30%)',
            }}
          />
          {/* Grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '50px 50px',
            }}
          />
          {/* Animated particles */}
          <div className="absolute inset-0">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-[#D4AF37]/30 animate-float-particle"
                style={{
                  left: `${20 + i * 15}%`,
                  top: `${30 + (i % 3) * 20}%`,
                  animationDelay: `${i * 0.5}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 text-center px-4 py-16 md:py-24">
          {/* Crown/Logo Icon - floating animation */}
          <div
            className={`flex justify-center mb-6 md:mb-8 transition-all duration-1000 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
            }`}
          >
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-b from-[#E5A500] to-[#D4AF37] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20 animate-float">
              <svg className="w-6 h-6 md:w-8 md:h-8 text-[#0A0A0A]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
              </svg>
            </div>
          </div>

          {/* Tagline */}
          <p
            className={`text-[10px] md:text-xs tracking-[4px] md:tracking-[6px] text-[#D4AF37] font-medium mb-4 md:mb-6 uppercase transition-all duration-1000 delay-100 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
          >
            {t('home.tagline')}
          </p>

          {/* Main Title with letter animation */}
          <h1
            className={`text-[60px] md:text-[110px] lg:text-[140px] font-black tracking-[6px] md:tracking-[12px] mb-3 md:mb-4 font-en leading-none transition-all duration-1000 delay-200 ${
              mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #F5F5F0 40%, #888888 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            <span className="inline-block animate-title-glow">BIGBANG</span>
          </h1>

          {/* Korean Subtitle */}
          <p
            className={`text-lg md:text-2xl text-[#888888] tracking-[3px] mb-4 md:mb-5 font-ko transition-all duration-1000 delay-300 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
          >
            {t('home.subtitle')}
          </p>

          {/* Description */}
          <p
            className={`text-sm md:text-base text-[#666666] max-w-[420px] md:max-w-[520px] mx-auto mb-8 md:mb-10 px-4 leading-relaxed transition-all duration-1000 delay-400 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
          >
            {t('home.description')}
          </p>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row justify-center gap-3 md:gap-4 mb-10 md:mb-14 px-4 transition-all duration-1000 delay-500 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
          >
            <Link
              href="/archive"
              className="group px-8 md:px-12 py-3.5 md:py-4 rounded-full bg-gradient-to-r from-[#E5A500] to-[#D4AF37] text-[#0A0A0A] font-bold text-sm tracking-wider hover:shadow-lg hover:shadow-[#D4AF37]/25 transition-all hover:scale-105 active:scale-95"
            >
              <span className="flex items-center justify-center gap-2">
                {t('home.enterArchive')}
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </Link>
            <Link
              href="/albums"
              className="px-8 md:px-12 py-3.5 md:py-4 rounded-full border-2 border-[#D4AF37]/40 text-[#D4AF37] font-semibold text-sm tracking-wider hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/60 transition-all hover:scale-105 active:scale-95"
            >
              {t('home.browseAlbums')}
            </Link>
          </div>

          {/* Member Colors Bar with wave animation */}
          <div
            className={`flex justify-center transition-all duration-1000 delay-600 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
          >
            <div className="flex gap-1 p-1 bg-white/5 rounded-full backdrop-blur-sm">
              {MEMBERS.map((member, index) => (
                <div
                  key={member.slug}
                  className="w-8 md:w-12 h-1.5 md:h-2 rounded-full transition-all hover:scale-110 animate-color-wave"
                  style={{
                    backgroundColor: member.color,
                    animationDelay: `${index * 0.1}s`,
                  }}
                  title={member.name}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Members Section */}
      <section ref={membersSection.ref} className="py-12 md:py-20 px-4 md:px-20 border-t border-white/5">
        <div className="max-w-[1280px] mx-auto">
          <div
            className={`flex items-center justify-center gap-4 mb-8 md:mb-12 transition-all duration-700 ${
              membersSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="h-px flex-1 max-w-[100px] bg-gradient-to-r from-transparent to-[#D4AF37]/30" />
            <h2 className="text-xs md:text-sm tracking-[6px] md:tracking-[8px] text-[#D4AF37] font-semibold">
              MEMBERS
            </h2>
            <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-[#D4AF37]/30" />
          </div>

          <div className="grid grid-cols-5 gap-3 md:gap-8 max-w-[900px] mx-auto">
            {MEMBERS.map((member, index) => (
              <Link
                key={member.slug}
                href={`/archive?member=${member.slug}`}
                className={`group flex flex-col items-center transition-all duration-700 ${
                  membersSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                {/* Member Avatar */}
                <div className="relative mb-2 md:mb-4">
                  {/* Outer ring with rotation on hover */}
                  <div
                    className="w-16 h-16 md:w-32 md:h-32 rounded-full p-[2px] md:p-[3px] transition-all duration-500 group-hover:scale-110 group-hover:rotate-3"
                    style={{
                      background: `linear-gradient(135deg, ${member.color}, ${member.color}80)`,
                      boxShadow: `0 4px 20px ${member.color}30`,
                    }}
                  >
                    {/* Inner circle with photo */}
                    <div className="w-full h-full rounded-full overflow-hidden relative bg-[#1A1A1A] group-hover:-rotate-3 transition-transform duration-500">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                  </div>

                  {/* Animated glow effect */}
                  <div
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-60 transition-all duration-500 -z-10 blur-2xl scale-110 group-hover:animate-pulse"
                    style={{ backgroundColor: member.color }}
                  />

                  {/* Sparkle effect on hover */}
                  <div className="absolute -top-1 -right-1 w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg viewBox="0 0 24 24" fill={member.color} className="animate-spin-slow">
                      <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10L12 0Z" />
                    </svg>
                  </div>
                </div>

                {/* Member Name */}
                <span
                  className="text-[10px] md:text-sm font-bold transition-all tracking-wider group-hover:tracking-widest duration-300"
                  style={{ color: member.color }}
                >
                  {t(`members.${member.slug}`)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresSection.ref} className="py-12 md:py-20 px-4 md:px-20">
        <div className="max-w-[1280px] mx-auto">
          <div
            className={`flex items-center justify-center gap-4 mb-8 md:mb-12 transition-all duration-700 ${
              featuresSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="h-px flex-1 max-w-[100px] bg-gradient-to-r from-transparent to-white/10" />
            <h2 className="text-xs md:text-sm tracking-[6px] md:tracking-[8px] text-[#666666] font-semibold">
              EXPLORE
            </h2>
            <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-white/10" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Archive Card */}
            <Link
              href="/archive"
              className={`group transition-all duration-700 ${
                featuresSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <div className="relative bg-gradient-to-br from-[#1A1A1A] to-[#0f0f0f] rounded-2xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-300 p-6 md:p-8 hover:translate-y-[-4px] hover:shadow-xl hover:shadow-[#D4AF37]/10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#D4AF37]/5 to-transparent rounded-bl-full transition-all duration-500 group-hover:w-48 group-hover:h-48" />

                <div className="relative flex flex-col md:flex-row gap-4 md:gap-6 items-start">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <svg className="w-7 h-7 md:w-8 md:h-8 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] md:text-xs text-[#D4AF37] tracking-[2px] mb-1 md:mb-2 font-en font-semibold">
                      SOCIAL MEDIA ARCHIVE
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-[#F5F5F0] mb-2 md:mb-3 group-hover:text-[#D4AF37] transition-colors">
                      {t('features.archiveTitle')}
                    </h3>
                    <p className="text-xs md:text-sm text-[#888888] leading-relaxed mb-4">
                      {t('features.archiveDesc')}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 md:gap-4 text-[10px] md:text-xs">
                      <span className="px-3 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] font-medium">
                        9,037 {t('common.posts')}
                      </span>
                      <span className="text-[#666666]">2014 - 2022</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Albums Card */}
            <Link
              href="/albums"
              className={`group transition-all duration-700 ${
                featuresSection.isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <div className="relative bg-gradient-to-br from-[#1A1A1A] to-[#0f0f0f] rounded-2xl overflow-hidden border border-white/5 hover:border-[#8FC126]/40 transition-all duration-300 p-6 md:p-8 hover:translate-y-[-4px] hover:shadow-xl hover:shadow-[#8FC126]/10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#8FC126]/5 to-transparent rounded-bl-full transition-all duration-500 group-hover:w-48 group-hover:h-48" />

                <div className="relative flex flex-col md:flex-row gap-4 md:gap-6 items-start">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-[#8FC126]/20 to-[#8FC126]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <svg className="w-7 h-7 md:w-8 md:h-8 text-[#8FC126]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] md:text-xs text-[#8FC126] tracking-[2px] mb-1 md:mb-2 font-en font-semibold">
                      DISCOGRAPHY
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-[#F5F5F0] mb-2 md:mb-3 group-hover:text-[#8FC126] transition-colors">
                      {t('features.albumsTitle')}
                    </h3>
                    <p className="text-xs md:text-sm text-[#888888] leading-relaxed mb-4">
                      {t('features.albumsDesc')}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 md:gap-4 text-[10px] md:text-xs">
                      <span className="px-3 py-1 rounded-full bg-[#8FC126]/10 text-[#8FC126] font-medium">
                        {t('albums.groupAndSolo')}
                      </span>
                      <span className="text-[#666666]">{t('albums.withLyrics')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section with counting animation */}
      <section ref={statsSection.ref} className="py-12 md:py-20 px-4 md:px-20 border-t border-white/5">
        <div className="max-w-[900px] mx-auto">
          <div className="grid grid-cols-3 gap-4 md:gap-12">
            <div
              className={`text-center transition-all duration-700 ${
                statsSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              <div className="inline-flex items-baseline gap-1">
                <p className="text-3xl md:text-5xl font-black text-[#F5F5F0] font-en">
                  <CountUp end={9037} isInView={statsSection.isInView} />
                </p>
              </div>
              <p className="text-[10px] md:text-sm text-[#666666] mt-1 md:mt-2">{t('home.postsCollected')}</p>
            </div>
            <div
              className={`text-center transition-all duration-700 ${
                statsSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <div className="inline-flex items-baseline gap-1">
                <p className="text-3xl md:text-5xl font-black text-[#F5F5F0] font-en">
                  <CountUp end={8} isInView={statsSection.isInView} />
                </p>
                <span className="text-lg md:text-2xl text-[#888888]">+</span>
              </div>
              <p className="text-[10px] md:text-sm text-[#666666] mt-1 md:mt-2">{t('home.yearsSpan')}</p>
            </div>
            <div
              className={`text-center transition-all duration-700 ${
                statsSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '300ms' }}
            >
              <div className="inline-flex items-baseline gap-1">
                <p className="text-3xl md:text-5xl font-black text-[#F5F5F0] font-en">
                  <CountUp end={5} isInView={statsSection.isInView} />
                </p>
              </div>
              <p className="text-[10px] md:text-sm text-[#666666] mt-1 md:mt-2">{t('home.memberFiles')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-10 md:py-14 px-4 md:px-20 border-t border-white/5">
        <div className="max-w-[1280px] mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="text-[#D4AF37] text-lg animate-pulse">&#9829;</span>
          </div>
          <p className="text-xs md:text-sm text-[#666666] mb-2">
            {t('home.footer')}
          </p>
          <p className="text-[10px] md:text-xs text-[#444444]">
            {t('home.vip')}
          </p>
        </div>
      </section>
    </div>
  );
}

// Count up animation component
function CountUp({ end, isInView }: { end: number; isInView: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [end, isInView]);

  return <>{count.toLocaleString()}</>;
}
